import React, { Component } from 'react';
import Alert from 'react-bootstrap/Alert';
import Form from 'react-bootstrap/Form';

import { DESCRIPTION_ROWS, TITLE_ROWS } from '../../components/autoGrow';
import GrowTextarea from '../../components/growTextarea';
import Composer from '../../../common/composer';
import { sameContent } from '../../../common/composer/content';
import CellDetailsView from './cellDetailsView';
import { isStructureEditable } from './cellRules';
import { getCell, upsertCell } from '../../domain/model';
import { cellFileBase } from '../../domain/schema';
import { fileContextFor } from '../../domain/cellObjects';

/**
 * CellDetailsSection — a cell's title and description, editable or not, and the rule for which.
 *
 * The two surfaces that show a cell — the mission map's dock and the cell's own screen — are one
 * click apart and read as one product. The dock could edit these while the cell was a draft and the
 * screen never could, so the same cell in the same state offered different things depending on how
 * you had arrived at it.
 *
 * The RULE lives here rather than in either of them, which is the point: a caller says what it
 * knows (`canEdit`, the revision and session gate) and this decides the rest. A caller that had to
 * remember `status === 'draft' && canEdit` is a caller that can forget it, and one of them did.
 *
 * Editing stops at draft exit because the orchestrator is walking the cell from then on — its brief
 * has already been dispatched, and rewriting it would describe work that is not the work being
 * done. Past that it renders CellDetailsView, the same read-only pair the dock has always shown.
 *
 * Saves on BLUR, not per keystroke: a title is written a word at a time and a write per keystroke
 * is a write per keystroke. An attachment is the exception — the upload has already happened, so adding
 * or removing a file writes at once. That makes TWO writers of one field, so writes of a field are
 * chained and the skip test reads the last value written rather than the prop (see _save); a refused
 * write is said out loud. The draft is re-seeded when the cell IDENTITY changes — not on every
 * prop change, which would discard what somebody was typing each time the heartbeat touched the
 * cell.
 *
 * Props:
 *   cell, missionId, client   — what to write and where
 *   onApplyLocal              — (updater) => void; reflect the write without a reload
 *   canEdit                   — false on a committed revision, a closed session, or no write access
 */
class CellDetailsSection extends Component {
  constructor(props) {
    super(props);
    const cellProps = (props.cell && props.cell.props) || {};
    this.state = {
      titleDraft: cellProps.title || '',
      descriptionDraft: cellProps.description,
      saveError: null,
    };
    // Per record and field: the tail of that field's write chain, and the value its last successful
    // write carried. See _save.
    this._writes = new Map();
    this._written = new Map();
  }

  componentDidUpdate(previous) {
    if ((previous.cell && previous.cell.element_id) !== (this.props.cell && this.props.cell.element_id)) {
      const cellProps = (this.props.cell && this.props.cell.props) || {};
      this.setState({
        titleDraft: cellProps.title || '',
        descriptionDraft: cellProps.description,
        // A failed write belongs to the cell it was for; carrying its message onto the next cell's
        // panel would read as that cell's failure.
        saveError: null,
      });
    }
  }

  // The record being edited, as a token the composer hands back with every report. Captured rather
  // than read at save time: the selection can move while an upload is in flight, and the reference
  // belongs to the cell the file was attached to.
  _target() {
    const { missionId, cell } = this.props;
    return { id: cell && cell.element_id, missionId, cell };
  }

  _isCurrent(target) {
    const now = this._target();
    return target.id === now.id && target.missionId === now.missionId;
  }

  /**
   * One write at a time per record and field, with the skip test against the last value WRITTEN rather
   * than against the prop.
   *
   * Two writes of `description` can be raised at once — an attach's upload finishing while a blur saves
   * the prose — and the prop does not move until the first of them resolves, so a prop-based test lets
   * both through and the server keeps whichever happens to land last. That is how a brief loses an
   * attachment whose chip is still on screen. `if_rev` is NOT the tool for this: a stale rev races the
   * heartbeat tick's own write and the server rejects it, which is why these paths are unconditional.
   */
  _save(field, next, target) {
    const to = target || this._target();
    if (!to.cell || !this.props.client) return Promise.resolve();
    const key = `${to.missionId}/${to.id}:${field}`;
    // `.catch` on the tail, not on the chain: _write reports its own failures, and this keeps one
    // unexpected throw from wedging every later write of the same field.
    const chain = (this._writes.get(key) || Promise.resolve())
      .catch(() => {})
      .then(() => this._write(key, field, next, to));
    this._writes.set(key, chain);
    return chain;
  }

  async _write(key, field, next, to) {
    const stored = this._written.has(key) ? this._written.get(key) : (to.cell.props || {})[field];
    if ('description' === field ? sameContent(next, stored) : next === (stored || '')) return;
    try {
      await this.props.client.setCellProps(to.missionId, to.id, { [field]: next });
      this._written.set(key, next);
      this._applyLocal(field, next, to);
      if (this.state.saveError) this.setState({ saveError: null });
    } catch (error) {
      // Not swallowed: what fails here can be a file whose chip is on screen, and a silent failure
      // leaves that chip standing over a brief the server never received.
      const message = (error && error.message) || 'The write did not go through.';
      this.setState({ saveError: `Could not save the ${field}. ${message}` });
    }
  }

  // Reflect the write locally (no reload), merging onto the record as the store holds it NOW rather
  // than onto the snapshot the write was raised with — a late write's snapshot can be minutes old.
  _applyLocal(field, next, to) {
    const { onApplyLocal } = this.props;
    if (!onApplyLocal) return;
    onApplyLocal(store => {
      const live = getCell(store, to.missionId, to.id) || to.cell;
      return upsertCell(store, to.missionId, { ...live, props: { ...(live.props || {}), [field]: next } });
    });
  }

  // A file's reference held back until blur is how bytes are stranded, so attach and remove write at once.
  // A report can name a cell the selection has already left — an upload that outlived it — and then the
  // DRAFT is left alone (it belongs to the cell on screen) while the write goes where the report says.
  _described = (content, { reason, owner }) => {
    const target = owner || this._target();
    if (this._isCurrent(target)) this.setState({ descriptionDraft: content });
    if ('edit' !== reason) this._save('description', content, target);
  };

  // A brief's files live under the cell: a draft cell has no meta thread to hang them off.
  _files() {
    const { client, missionId, cell } = this.props;
    if (!client || !client.uploadFile || !cell) return {};
    return {
      base: cellFileBase(missionId, cell.element_id),
      upload: ({ alias, name, contentType, bytes, file }) =>
        client.uploadFile(alias, name, contentType, bytes, file),
    };
  }

  render() {
    const { cell, canEdit, collapsed } = this.props;
    if (!isStructureEditable(cell) || canEdit === false) {
      return <CellDetailsView cell={cell} collapsed={collapsed} fileContext={fileContextFor(this.props.client)} />;
    }
    const { titleDraft, descriptionDraft } = this.state;
    const files = this._files();
    return (
      <>
        {/* TITLE and DESCRIPTION are what these fields are called on every surface that touches
            them — these panels, the orchestrator and the seeds — so the words
            a person reads here are the words the rest of the system uses. */}
        <Form.Group>
          <GrowTextarea
            rows={1}
            maxRows={TITLE_ROWS}
            value={titleDraft}
            placeholder="Cell title…"
            onChange={value => this.setState({ titleDraft: value })}
            onBlur={() => this._save('title', titleDraft)}
          />
          {!collapsed && <Form.Text muted>What this cell is called.</Form.Text>}
        </Form.Group>
        {/* Collapsing Details keeps the TITLE: it is the cell's name, and a section that hid it
            would leave a header called "Details" standing in for one. The description is the part
            that costs the screen, so it is the part that goes. The draft above is untouched — this
            section stays mounted while it is collapsed, so half-typed prose survives a fold. */}
        {!collapsed && (
        <Form.Group>
          <Composer
            ref={this.props.composerRef}
            value={descriptionDraft}
            owner={this._target()}
            onChange={this._described}
            onBlur={() => this._save('description', this.state.descriptionDraft)}
            placeholder="Cell description…"
            maxRows={DESCRIPTION_ROWS}
            uploadWhen="stage"
            fileBase={files.base}
            upload={files.upload}
            fileContext={fileContextFor(this.props.client)}
          />
          <Form.Text muted>What this cell is about.</Form.Text>
        </Form.Group>
        )}
        {/* A refused write, said out loud — the same small warning the Actions cards use, because it is the
            same kind of news: something you asked for did not happen. OUTSIDE THE FOLD, exactly as in the
            mission panel: folding Details must not be what hides the news that a write failed. */}
        {this.state.saveError && (
          <Alert variant="warning" style={{ fontSize: '0.8rem', marginBottom: '1rem' }}>
            {this.state.saveError}
          </Alert>
        )}
      </>
    );
  }
}

export default CellDetailsSection;
